import React, { useState, useEffect, Suspense, useRef } from "react";
import Navbar from "../Navbar";
import { Link, NavLink } from "react-router-dom";
import Footer from "../Footer";
import DownloadAfriPay from "../DownloadAfriPay";
import { DashboardApi } from '../../apis/DashboardApi';
import { AxiosResponse } from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import WhyUseAfriPay from "../WhyUseAfriPay";
import ConversionBG from "../../img/conversionBG.avif"
import { HiOutlineSwitchHorizontal, HiOutlineSwitchVertical } from "react-icons/hi";
import { FaAngleDown, FaAngleUp, FaRegArrowAltCircleRight } from "react-icons/fa";
import { Accordion, AccordionItem, AccordionItemButton, AccordionItemHeading, AccordionItemPanel, AccordionItemState } from "react-accessible-accordion";
import { motion } from "framer-motion";
import HeroCTA2 from "../HeroCTA2";
import HeroCTA1 from "../HeroCTA1";
import { useDownloadAppModal } from "../../context/DownloadAppModalContext";

function CurrencyConverter() {
  const { openDownloadModal } = useDownloadAppModal();
  const [fromCurrency, setFromCurrency] = useState("NGN");
  const [toCurrency, setToCurrency] = useState("USD");
  const [amount, setAmount] = useState("0");
  const [exchangeRate, setExchangeRate] = useState(0);
  const [convertedAmount, setConvertedAmount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [allRates, setAllRates] = useState<any>(null);
  const [isLoadingLiveRates, setIsLoadingLiveRates] = useState(false);
  const [rateDisplay, setRateDisplay] = useState("");
  const [targetAmount, setTargetAmount] = useState("");

  const currencies = [
    { code: "NGN", name: "Nigeria Naira", flag: "/images/NG.svg", symbol: "₦" },
    { code: "USD", name: "US Dollar", flag: "/images/US.svg", symbol: "$" },
    // { code: "GBP", name: "British Pound", flag: "🇬🇧", symbol: "£" },
    { code: "EUR", name: "Euro", flag: "/images/EU.svg", symbol: "€" },
    // { code: "GHS", name: "Ghana Cedi", flag: "🇬🇭", symbol: "₵" },
    { code: "CAD", name: "Canada Dollar", flag: "/images/CA.svg", symbol: "$" }
  ];

  const getCurrencySymbol = (code: any) => {
    return currencies.find(c => c.code === code)?.symbol || code;
  };

  const getCurrencyFlag = (currency: string) => {
    const flags: any = {
      'USD': '/images/US.svg',
      'NGN': '/images/NG.svg',
      'EUR': '/images/EU.svg',
      'GBP': '/images/GB.svg',
      'CAD': '/images/CA.svg',
      'GHS': '/images/GH.svg'
    };
    return flags[currency] || '🌍';
  };

  const handleSwap = () => {
    setFromCurrency(toCurrency);
    setToCurrency(fromCurrency);
    setExchangeRate(1 / exchangeRate);
  };

  const handleCheckRate = () => {


    if (!amount || parseFloat(amount) <= 0) {
      console.log('❌ Amount validation failed');
      toast.error("Please enter a valid amount");
      return;
    }


    setLoading(true);

    const requestData = {
      source_amount: parseFloat(amount),
      source_currency: fromCurrency,
      target_currency: toCurrency
    };

    console.log('📤 Sending request:', requestData);

    DashboardApi.getLatestRatesOnWebsite(requestData)
      .then((response: AxiosResponse<any>) => {
        console.log('📥 Received response:', response);

        if (response?.data) {
          console.log('✅ Response validation passed');
          const rateKey = `${fromCurrency}_TO_${toCurrency}`;
          console.log('🔑 Looking for rate key:', rateKey);
          console.log('📊 Available data:', response.data.data);

          const rateData = response.data.data[rateKey];
          console.log('💰 Rate data found:', rateData);

          if (rateData) {
            // Just store the rate_display from API - no calculations
            setRateDisplay(rateData.rate_display);
            setTargetAmount(rateData.target_amount)
            setExchangeRate(rateData.rate); // Keep this if you need it elsewhere
          } else {
            console.log('❌ Rate data NOT found for key:', rateKey);
            toast.error("Exchange rate not available for selected currencies");
          }
        } else {
          console.log('❌ Response validation failed');
          console.log('Response data:', response?.data);
        }
        setLoading(false);
      })
      .catch(error => {
        console.error("❌ Error fetching exchange rate:", error);
        console.error("Error details:", error.response || error.message);
        toast.error("Failed to fetch exchange rate. Please try again.");
        setLoading(false);
      });
  };

  const fetchAllRates = () => {
    setIsLoadingLiveRates(true);
    DashboardApi.getAllRatesOnWebsite()
      .then((response: AxiosResponse<any>) => {
        if (response?.data) {
          setAllRates(response?.data?.data);
          console.log(response?.data?.data)
        }
        setIsLoadingLiveRates(false);
      })
      .catch(error => {
        console.error("Error fetching live rates:", error);
        setIsLoadingLiveRates(false);
      });
  };

  const getFeaturedRates = () => {
    if (!allRates?.rates) return [];

    const priorityPairs = ['USD_TO_NGN', 'EUR_TO_NGN', 'GBP_TO_NGN', 'NGN_TO_USD', 'USD_TO_GHS', 'NGN_TO_GHS'];
    const ratesArray = Object.entries(allRates.rates).map(([key, value]) => ({
      key,
      ...value as any
    }));

    const featured = priorityPairs
      .map(pair => ratesArray.find(r => r.key === pair))
      .filter(Boolean);

    // Add remaining rates that aren't in priority pairs
    ratesArray.forEach(rate => {
      if (!featured.some(f => f.key === rate.key)) {
        featured.push(rate);
      }
    });

    return featured; // Return all rates, not limited to 6
  };



  useEffect(() => {
    fetchAllRates();

    // Refresh rates every 30 minutes
    const intervalId = setInterval(() => {
      fetchAllRates();
    }, 30 * 60 * 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Animation refs
  const heroRef = useRef(null);
  const converterRef = useRef(null);
  const liveRatesRef = useRef(null);
  const howItWorksRef = useRef(null);
  const featuresRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry: any) => {
        if (entry.isIntersecting) {
          // Hero animation
          if (entry.target === heroRef.current) {
            const elements = entry.target.querySelectorAll('.hero-item')
            elements.forEach((element: any, index: any) => {
              setTimeout(() => {
                element.classList.add('animate-fade-in-up')
              }, index * 150)
            })
          }

          // Converter card animation
          if (entry.target === converterRef.current) {
            const card = entry.target.querySelector('.converter-card')
            if (card) {
              card.classList.add('animate-slide-up-fade')
            }
          }

          // Live rates animation
          if (entry.target === liveRatesRef.current) {
            const title = entry.target.querySelector('.section-title')
            if (title) {
              title.classList.add('animate-fade-in-up')
            }

            const cards = entry.target.querySelectorAll('.rate-card')
            cards.forEach((card: any, index: any) => {
              setTimeout(() => {
                card.classList.add('animate-scale-in')
              }, (index + 1) * 80)
            })
          }

          // How it works animation
          if (entry.target === howItWorksRef.current) {
            const title = entry.target.querySelector('.section-title')
            if (title) {
              title.classList.add('animate-fade-in-up')
            }

            const steps = entry.target.querySelectorAll('.step-card')
            steps.forEach((step: any, index: any) => {
              setTimeout(() => {
                step.classList.add('animate-slide-in-up')
              }, (index + 1) * 200)
            })

            const button = entry.target.querySelector('.cta-button')
            setTimeout(() => {
              if (button) button.classList.add('animate-scale-in')
            }, 800)
          }

          // Features animation
          if (entry.target === featuresRef.current) {
            const title = entry.target.querySelector('.section-title')
            const subtitle = entry.target.querySelector('.section-subtitle')

            if (title) {
              title.classList.add('animate-fade-in-up')
            }

            setTimeout(() => {
              if (subtitle) subtitle.classList.add('animate-fade-in-up')
            }, 200)
          }
        }
      })
    }, observerOptions)

    const refs = [heroRef, converterRef, liveRatesRef, howItWorksRef, featuresRef]
    refs.forEach(ref => {
      if (ref.current) observer.observe(ref.current)
    })

    return () => observer.disconnect()
  }, [])

  const slideInLeft = {
    hidden: { opacity: 0, x: -80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const slideInRight = {
    hidden: { opacity: 0, x: 80 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.8,
        ease: "easeOut",
      },
    },
  };

  const container = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 40,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  const fadeUpImage = {
    hidden: { opacity: 0, y: 40, scale: 0.96 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.7, ease: "easeOut" },
    },
  };


  return (
    <>
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideUpFade {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(40px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes scaleIn {
          from {
            opacity: 0;
            transform: scale(0.9);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-slide-up-fade {
          animation: slideUpFade 1s ease-out forwards;
        }

        .animate-slide-in-up {
          animation: slideInUp 0.8s ease-out forwards;
        }

        .animate-scale-in {
          animation: scaleIn 0.8s ease-out forwards;
        }

        .initial-hidden {
          opacity: 0;
        }

        

        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        .marquee-track:hover {
          animation-play-state: paused;
        }
      `}</style>

      <Navbar />

      <div className=" pt-14">
        {/* Green Header Section */}
        <div className="bg-white max-w-7xl mx-auto py-14" ref={heroRef}>
          <motion.div variants={container} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="flex flex-col lg:flex-row items-center justify-between rounded-3xl py-12 px-10" style={{ backgroundImage: `url(${ConversionBG})`, backgroundRepeat: "no-repeat", backgroundSize: "cover", backgroundPosition: "center" }}>
            <motion.div variants={slideInLeft} className="w-[300px] md:w-[512px] text-center lg:text-left mb-6 lg:mb-0">
              <h1 className="text-white text-2xl md:text-[60px] font-display-fran font-semibold mb-6 leading-none">Real-Time Rates For Conversion</h1>
              <p className="text-base md:text-lg text-white mb-11">Real-time exchange rates. Low conversion fees (0.5% - 2%). Lock rates for 24 hours.</p>
              <button className="w-32 h-13 p-3.5 text-sm text-white bg-[#0B7F47] border-2 border-[#7FBC51] rounded-xl w-36" onClick={openDownloadModal}>Get started now!</button>
            </motion.div>
            <motion.div variants={slideInRight} className="flex flex-col items-center rounded-2xl bg-white px-6 pt-6 pb-12">
              <h1 className="font-semibold text-lg mb-3 text-left w-full">Currency Exchange Calculator</h1>
              <div className="mb-3 w-full">
                <label className="text-[#2C3036] mb-1 text-xs">Amount to convert</label>
                <div className="flex items-center justify-between w-full h-13 border border-[#E9E9E9] p-4 rounded-lg">
                  <input
                    type="text"
                    value={`${getCurrencySymbol(fromCurrency)} ${amount}`}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      console.log('Amount input changed to:', value);
                      setAmount(value);
                    }}
                    className="w-full bg-transparent border-0 px-0 py-2 text-base font-normal text-[#030319] focus:outline-none"
                    placeholder="0"
                  />
                  <div className="relative">
                    {/* Flag Image */}
                    <img
                      src={currencies.find(c => c.code === fromCurrency)?.flag}
                      alt={`${fromCurrency} flag`}
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 rounded-sm pointer-events-none z-10"
                    />

                    <select
                      value={fromCurrency}
                      onChange={(e) => setFromCurrency(e.target.value)}
                      className="lg:w-[230px] w-full appearance-none bg-transparent border-0 pl-10 pr-8 py-2 text-base font-medium text-[#030319] focus:outline-none cursor-pointer"
                    >
                      {currencies.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              {/* Swap Button */}
              <div className="flex justify-center items-center pb-2">
                <button
                  onClick={handleSwap}
                  className="bg-white border-2 border-gray-200 hover:border-[#26A869] rounded-full p-3 transition-all duration-200 hover:rotate-180 transform"
                  title="Swap currencies"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                  </svg>
                </button>
              </div>

              <div className="w-full">
                <label className="text-[#2C3036] mb-1 text-xs">What you’ll get</label>
                <div className="flex items-center justify-between w-full h-13 border border-[#E9E9E9] p-4 rounded-lg">
                  <span className="text-[14px] font-[500] text-gray-800">
                    {targetAmount
                      ? `${getCurrencySymbol(toCurrency)}${parseFloat(targetAmount).toFixed(2)}`
                      : `${getCurrencySymbol(toCurrency)}0.00`
                    }
                  </span>
                  <div className="relative">
                    {/* Flag Image */}
                    <img
                      src={currencies.find(c => c.code === toCurrency)?.flag}
                      alt={`${toCurrency} flag`}
                      className="absolute left-0 top-1/2 -translate-y-1/2 w-5 h-5 rounded-sm pointer-events-none z-10"
                    />

                    <select
                      value={toCurrency}
                      onChange={(e) => setToCurrency(e.target.value)}
                      className="w-full appearance-none bg-transparent border-0 pl-10 pr-4 py-2 text-base font-medium text-[#030319] focus:outline-none cursor-pointer"
                    >
                      {currencies.map((currency) => (
                        <option key={currency.code} value={currency.code}>
                          {currency.code} - {currency.name}
                        </option>
                      ))}
                    </select>

                    <div className="absolute right-0 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>

              <button
                onClick={handleCheckRate}
                disabled={loading}
                className="bg-[#01331B] hover:bg-[#01331B50] text-white font-medium text-[14px] px-8 py-3 rounded-full transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap my-4"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Checking...
                  </span>
                ) : (
                  "Check Rate"
                )}
              </button>

              {targetAmount && <div className="bg-[#01331B] rounded-lg w-full p-4 text-white text-sm">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="">Exchange Rate</h3>
                  <span className="font-semibold">
                    ({rateDisplay || `${getCurrencySymbol(toCurrency)}0.00`})
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <h3 className="">Fee (1.5%)</h3>
                  <span className="font-semibold">
                    {(parseFloat(targetAmount) * 0.015).toFixed(2)} {toCurrency}
                  </span>
                </div>
              </div>}
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Our Live Rate */}
      <div className="pt-12 pb-16 bg-[#01331B]" ref={liveRatesRef}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col items-center">
          {/* Section Title */}
          <div className="flex flex-col justify-center items-center mb-11 text-center text-white">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold font-display-fran mb-3 text-center">
              Live Exchange Rates
            </h3>
            <p className="text-2xl">Updated Every Minute. Best Rates Guaranteed</p>
          </div>

          {/* Rate Cards Grid */}
          {isLoadingLiveRates ? (
            <div className="flex justify-center items-center py-10">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#26A869]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {getFeaturedRates().map((rate: any, index: number) => (
                <div
                  key={index}
                  // REMOVE initial-hidden class, add opacity-0 and animate on mount
                  className="rate-card bg-[#FFFBEB33] rounded-xl pt-6 pb-4 px-8 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col items-center"
                  style={{
                    animation: `scaleIn 0.8s ease-out ${index * 0.08}s forwards`,
                    opacity: 0
                  }}
                >

                  <div className="flex flex-col items-center text-center gap-4">
                    <div className="flex items-center gap-2 text-base font-bold text-white">
                      <p>{rate.source_currency}</p>
                      <HiOutlineSwitchHorizontal color="#7FBC51" size={16} />
                      <p>{rate.target_currency}</p>
                    </div>
                    <h3 className="text-xl md:text-[30px] text-white text-bold">{rate.target_amount}</h3>
                    <p className="text-sm text-white">{rate.rate_display}</p>
                  </div>
                  {/* Rest of the card content */}
                  {/* <div className="flex flex-col items-start justify-between">
                    <p className="text-[12px] md:text-lg font-medium text-gray-900 mb-4">{rate.rate_display}</p>
                    <div className="flex items-center">
                      <img src={getCurrencyFlag(rate.source_currency)} alt={`${rate.source_currency} flag`} width={30} />
                      <img src={getCurrencyFlag(rate.target_currency)} alt={`${rate.target_currency} flag`} width={30} className="-ml-1" />
                    </div>
                  </div> */}

                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <motion.section variants={container} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} className="max-w-7xl mx-auto flex flex-col items-center pt-20 pb-4">
        <motion.h2 variants={fadeUp} className="text-[#030319] text-3xl font-bold font-display-fran mb-3">Transparent Fee Structure</motion.h2>
        <motion.p variants={fadeUp} className="text-[#344054] text-2xl mb-4">No hidden Charges. Know exactly what you pay</motion.p>
        <div>
          <motion.img variants={fadeUpImage} src="/images/fee_structure.avif" alt="Fee Structure" className="hidden md:block max-w-[961px]" />
          <motion.img variants={fadeUpImage} src="/images/fee_structure_mobile.avif" alt="Fee Structure" className="w-full block md:hidden" />
        </div>
      </motion.section>

      {/* FAQ SECTION */}
      <section className="w-full px-4 pt-10 md:pt-20 pb-10">
        <div className="max-w-7xl mx-auto">
          <div className="w-full flex flex-col md:flex-row items-start md:items-end justify-between mb-8 font-display-fran">
            <div className="flex flex-col text-left gap-3 mb-4 md:mb-0">
              <h2 className="text-2xl sm:text-2xl lg:text-4xl text-[#030319] font-display font-bold">
                Everything You Need to Know
              </h2>
              <p className="text-[#344054] text-lg">Discover how AfriPay works and what makes us different</p>
            </div>
            <Link to={'/faq'} className="text-[#024529] flex items-center justify-center gap-1.5 cursor-pointer transition-all duration-300 hover:text-gray-600">
              View All <FaRegArrowAltCircleRight />
            </Link>
          </div>
          <div className="gap-8 lg:mt-16 mt-8 ">
            <div className="lg:col-span-8 col-span-12">
              <Accordion allowZeroExpanded allowMultipleExpanded>
                <AccordionItem className="border border-[#FFFFFF12] overflow-hidden shadow-md rounded-lg mb-4">
                  <AccordionItemHeading>
                    <AccordionItemState>
                      {({ expanded }) => (
                        <AccordionItemButton
                          className={`py-4 px-4 text-base sm:text-lg text-[#0a0a0a] font-display font-semibold flex items-center justify-between focus:outline-none focus:ring-offset-2`}
                          aria-expanded={expanded}
                        >
                          <span>What is AfriPay?</span>
                          <span className="w-7 h-7 rounded-full flex items-center justify-center border border-[#E7E8E9] shadow-sm">
                            {expanded ? <FaAngleUp color="#637381" size={16} /> : <FaAngleDown color="#637381" size={16} />}
                          </span>
                        </AccordionItemButton>
                      )}
                    </AccordionItemState>
                  </AccordionItemHeading>
                  <AccordionItemPanel className="pb-6 px-4 text-xs sm:text-sm text-[#101828CC] font-body font-medium leading-relaxed">
                    <p className="w-[940px]">
                      AfriPay is an innovative platform simplifying
                      international tuition payments, securing educational
                      loans, and offering a digital wallet feature for
                      seamless financial transactions.
                    </p>
                  </AccordionItemPanel>
                </AccordionItem>

                <AccordionItem className="border border-[#FFFFFF12] overflow-hidden shadow-md rounded-lg mb-4">
                  <AccordionItemHeading>
                    <AccordionItemState>
                      {({ expanded }) => (
                        <AccordionItemButton
                          className={`py-4 px-4 text-base sm:text-lg text-[#0a0a0a] font-display font-semibold flex items-center justify-between focus:outline-none focus:ring-offset-2`}
                          aria-expanded={expanded}
                        >
                          <span>How does AfriPay simplify tuition payments?</span>
                          <span className="w-7 h-7 rounded-full flex items-center justify-center border border-[#E7E8E9] shadow-sm">
                            {expanded ? <FaAngleUp color="#637381" size={16} /> : <FaAngleDown color="#637381" size={16} />}
                          </span>
                        </AccordionItemButton>
                      )}
                    </AccordionItemState>
                  </AccordionItemHeading>
                  <AccordionItemPanel className="pb-6 px-4 text-xs sm:text-sm text-[#101828CC] font-body font-medium leading-relaxed">
                    <p className="w-[940px]">
                      AfriPay streamlines the tuition payment process, providing a user-friendly interface and multiple payment options for efficient and secure transactions.
                    </p>
                  </AccordionItemPanel>
                </AccordionItem>

                <AccordionItem className="border border-[#FFFFFF12] overflow-hidden shadow-md rounded-lg mb-4">
                  <AccordionItemHeading>
                    <AccordionItemState>
                      {({ expanded }) => (
                        <AccordionItemButton
                          className={`py-4 px-4 text-base sm:text-lg text-[#0a0a0a] font-display font-semibold flex items-center justify-between focus:outline-none focus:ring-offset-2`}
                          aria-expanded={expanded}
                        >
                          <span>What security measures does AfriPay have in place?</span>
                          <span className="w-7 h-7 rounded-full flex items-center justify-center border border-[#E7E8E9] shadow-sm">
                            {expanded ? <FaAngleUp color="#637381" size={16} /> : <FaAngleDown color="#637381" size={16} />}
                          </span>
                        </AccordionItemButton>
                      )}
                    </AccordionItemState>
                  </AccordionItemHeading>
                  <AccordionItemPanel className="pb-6 px-4 text-xs sm:text-sm text-[#101828CC] font-body font-medium leading-relaxed">
                    <p className="w-[940px]">
                      AfriPay employs cutting-edge encryption technologies to ensure the security and confidentiality of user data, providing a safe environment for financial transactions.
                    </p>
                  </AccordionItemPanel>
                </AccordionItem>

                <AccordionItem className="border border-[#FFFFFF12] overflow-hidden shadow-md rounded-lg mb-4">
                  <AccordionItemHeading>
                    <AccordionItemState>
                      {({ expanded }) => (
                        <AccordionItemButton
                          className={`py-4 px-4 text-base sm:text-lg text-[#0a0a0a] font-display font-semibold flex items-center justify-between focus:outline-none focus:ring-offset-2`}
                          aria-expanded={expanded}
                        >
                          <span>What is the digital wallet feature in AfriPay?</span>
                          <span className="w-7 h-7 rounded-full flex items-center justify-center border border-[#E7E8E9] shadow-sm">
                            {expanded ? <FaAngleUp color="#637381" size={16} /> : <FaAngleDown color="#637381" size={16} />}
                          </span>
                        </AccordionItemButton>
                      )}
                    </AccordionItemState>
                  </AccordionItemHeading>
                  <AccordionItemPanel className="pb-6 px-4 text-xs sm:text-sm text-[#101828CC] font-body font-medium leading-relaxed">
                    <p className="w-[940px]">
                      The digital wallet feature allows users to conveniently manage and access funds, enabling cashless transactions, budgeting, and automated savings.
                    </p>
                  </AccordionItemPanel>
                </AccordionItem>

                <AccordionItem className="border border-[#FFFFFF12] overflow-hidden shadow-md rounded-lg mb-4">
                  <AccordionItemHeading>
                    <AccordionItemState>
                      {({ expanded }) => (
                        <AccordionItemButton
                          className={`py-4 px-4 text-base sm:text-lg text-[#0a0a0a] font-display font-semibold flex items-center justify-between focus:outline-none focus:ring-offset-2`}
                          aria-expanded={expanded}
                        >
                          <span>How does AfriPay support educational loans?</span>
                          <span className="w-7 h-7 rounded-full flex items-center justify-center border border-[#E7E8E9] shadow-sm">
                            {expanded ? <FaAngleUp color="#637381" size={16} /> : <FaAngleDown color="#637381" size={16} />}
                          </span>
                        </AccordionItemButton>
                      )}
                    </AccordionItemState>
                  </AccordionItemHeading>
                  <AccordionItemPanel className="pb-6 px-4 text-xs sm:text-sm text-[#101828CC] font-body font-medium leading-relaxed">
                    <p className="w-[940px]">
                      AfriPay assists users in securing educational loans, streamlining the application process, and providing transparent terms for quick approvals.
                    </p>
                  </AccordionItemPanel>
                </AccordionItem>

              </Accordion>

            </div>
          </div>
          <div className="w-full flex justify-center pt-5">
            <button type="button" onClick={openDownloadModal} className="w-[174px] h-13 rounded-lg bg-[#0B7F47] border-2 border-[#7FBC51] shadow-sm text-white text-sm p-3.5 cursor-pointer transition-colors duration-300 hover:bg-[#0B7F47]/60">Get started — it's free</button>
          </div>
        </div>
      </section>

      <div className='max-w-7xl mx-auto mb-4 lg:mb-8'>
        <HeroCTA1 />
        <HeroCTA2 />
      </div>

      {/* Download AfriPay App */}
      <DownloadAfriPay />

      {/* <-- Footer --> */}
      <Footer />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </>
  )
}

export default CurrencyConverter